<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useCreateOrg } from "@/queries/orgs";

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "created"): void;
}>();

const { createOrg } = useCreateOrg();

const orgName = ref("");
const isSubmitting = ref(false);
const errorMessage = ref("");
const touched = ref(false);

// Validation
const isValidOrgName = computed(() => {
  return orgName.value.trim().length >= 3;
});

const validationMessage = computed(() => {
  if (!touched.value) return "";
  if (orgName.value.trim().length === 0) {
    return "Organisation name is required";
  }
  if (orgName.value.trim().length < 3) {
    return "Organisation name must be at least 3 characters";
  }
  return "";
});

const canSubmit = computed(() => {
  return isValidOrgName.value && !isSubmitting.value;
});

// Reset form when modal is closed
watch(
  () => props.show,
  (newShow) => {
    if (!newShow) {
      resetForm();
    }
  }
);

function resetForm() {
  orgName.value = "";
  errorMessage.value = "";
  touched.value = false;
}

function closeModal() {
  emit("update:show", false);
}

function handleSubmit() {
  touched.value = true;

  if (!canSubmit.value) {
    return;
  }

  errorMessage.value = "";
  isSubmitting.value = true;

  createOrg(orgName.value.trim(), {
    onSuccess: () => {
      emit("created");
      closeModal();
    },
    onError: (e: any) => { errorMessage.value = e?.userMessage ?? "Failed to create organisation"; },
    onSettled: () => { isSubmitting.value = false; },
  });
}

function handleInput() {
  if (orgName.value.length > 0) {
    touched.value = true;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">
              <i class="bi bi-building-add me-2"></i>Create Organisation
            </h2>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
              :disabled="isSubmitting"
            ></button>
          </div>

          <form @submit.prevent="handleSubmit">
            <div class="modal-body">
              <div class="alert alert-info" role="alert">
                <i class="bi bi-info-circle me-2"></i>
                Creating a new organisation will set up an empty workspace for a
                new customer or team.
              </div>

              <!-- Error Alert -->
              <div v-if="errorMessage" class="alert alert-danger" role="alert">
                <i class="bi bi-exclamation-triangle me-2"></i>
                {{ errorMessage }}
              </div>

              <!-- Organisation Name Input -->
              <div class="mb-3">
                <label for="orgName" class="form-label">
                  Organisation Name <span class="text-danger">*</span>
                </label>
                <input
                  id="orgName"
                  v-model="orgName"
                  type="text"
                  class="form-control"
                  :class="{
                    'is-invalid': validationMessage && touched,
                    'is-valid': isValidOrgName && touched,
                  }"
                  placeholder="Enter organisation name"
                  maxlength="100"
                  :disabled="isSubmitting"
                  @input="handleInput"
                  @blur="touched = true"
                  autofocus
                />
                <div v-if="validationMessage && touched" class="invalid-feedback">
                  {{ validationMessage }}
                </div>
                <div class="form-text">
                  Minimum 3 characters. This will be visible to users in the
                  organisation.
                </div>
              </div>

              <!-- Character Counter -->
              <div class="text-end text-muted small mb-2">
                {{ orgName.length }}/100 characters
              </div>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="bb-btn-cancel"
                @click="closeModal"
                :disabled="isSubmitting"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="bb-btn-primary-cta"
                :disabled="!canSubmit"
              >
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                <i v-else class="bi bi-plus-circle me-2"></i>
                {{ isSubmitting ? "Creating..." : "Create Organisation" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div v-if="show" class="modal-backdrop fade show" @click="closeModal"></div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 500px;
}

.form-label {
  font-weight: 600;
}

.btn:disabled {
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>
